/**
 * The tile renderer: turns portable face geometry into a tile with a body.
 *
 * One component covers all 34 faces plus the back, at every size, with the
 * hover / legal / selected / lifted states the table needs.
 *
 * The *face* is SVG, because the artwork has to stay crisp at any size and
 * portable to a future Android renderer. The *body* is CSS on the element that
 * wraps it: a stack of hard offset shadows that reads as ivory thickness, a
 * chamfer at the bottom edge, and a soft contact shadow where the tile meets
 * whatever it is resting on. Keeping thickness in CSS means it can respond to
 * state - a lifted tile grows its shadow, a tile lying on the felt flattens it -
 * without re-rendering a single path.
 *
 * The real pixel size comes from CSS custom properties, so the whole table
 * scales with the viewport without React re-rendering a single tile.
 *
 * The artwork is shared by both languages; only the name a screen reader hears
 * changes, and the internal tile kind is exposed as `data-kind` so automation
 * never has to read a translated label.
 */

import type { CSSProperties } from "react";
import type { TileKind } from "@kikik/mahjong-core";
import { useI18n } from "../../i18n";
import {
  TILE_HEIGHT,
  TILE_WIDTH,
  faceIndex,
  faceInk,
  faceShapes,
} from "./geometry";
import type { Ink, Shape } from "./geometry";

export type TileSize = "xs" | "sm" | "md" | "lg";

/** Fallback width in px; CSS overrides it per size and per breakpoint. */
const SIZE_WIDTH: Record<TileSize, number> = { xs: 22, sm: 30, md: 42, lg: 54 };

/** A tile seen edge-on from the side of the table, i.e. rotated a quarter turn. */
export type TileOrientation = "up" | "side";

/** Ivory body, a soft sheen at the top and a bevel that reads as thickness. */
const BODY = { x: 1, y: 1, w: TILE_WIDTH - 2, h: TILE_HEIGHT - 2, r: 7 };
const GLOSS_PATH = `M3.5 74 V8 A4.5 4.5 0 0 1 8 3.5 H52`;
const BEVEL_PATH = `M56.5 10 V75 A4.5 4.5 0 0 1 52 79.5 H8`;

/**
 * Light falling from above, faked as five stacked bands instead of a gradient.
 *
 * A <linearGradient> would need an id, and an id repeated across the hundred-odd
 * tiles on a table is a document full of duplicates. Five bands whose opacity
 * steps by a few percent cost nothing, need no defs, and at the size a tile
 * actually renders each step is well under a pixel, so it reads as a gradient.
 */
const SHEEN_BANDS: readonly string[] = [
  "M1 8 A7 7 0 0 1 8 1 H52 A7 7 0 0 1 59 8 V15 H1 Z",
  "M1 15 H59 V24 H1 Z",
  "M1 24 H59 V33 H1 Z",
  "M1 33 H59 V42 H1 Z",
  "M1 42 H59 V51 H1 Z",
];

function sheen() {
  return SHEEN_BANDS.map((d, index) => (
    <path key={index} d={d} className={`tile__sheen tile__sheen--${index + 1}`} />
  ));
}

function inkVar(ink: Ink): string {
  return `var(--tile-${ink})`;
}

function renderShape(shape: Shape, index: number) {
  switch (shape.type) {
    case "disc":
      return (
        <g key={index}>
          <circle cx={shape.cx} cy={shape.cy} r={shape.r} fill={inkVar(shape.ink)} opacity={0.16} />
          {shape.ring ? (
            <circle
              cx={shape.cx}
              cy={shape.cy}
              r={shape.r}
              fill="none"
              stroke={inkVar(shape.ink)}
              strokeWidth={2}
            />
          ) : (
            <circle cx={shape.cx} cy={shape.cy} r={shape.r} fill={inkVar(shape.ink)} />
          )}
        </g>
      );
    case "stick":
      return (
        <g key={index}>
          <rect
            x={shape.cx - 3.5}
            y={shape.cy - shape.height / 2}
            width={7}
            height={shape.height}
            rx={3.5}
            fill={inkVar(shape.ink)}
            opacity={0.18}
          />
          <rect
            x={shape.cx - 3.5}
            y={shape.cy - shape.height / 2}
            width={7}
            height={shape.height}
            rx={3.5}
            fill="none"
            stroke={inkVar(shape.ink)}
            strokeWidth={1.8}
          />
          <line
            x1={shape.cx - 3.5}
            y1={shape.cy}
            x2={shape.cx + 3.5}
            y2={shape.cy}
            stroke={inkVar(shape.ink)}
            strokeWidth={1.6}
          />
        </g>
      );
    case "stroke":
      return (
        <path
          key={index}
          d={shape.d}
          fill="none"
          stroke={inkVar(shape.ink)}
          strokeWidth={shape.width ?? 2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case "fill":
      return <path key={index} d={shape.d} fill={inkVar(shape.ink)} />;
    case "glyphBox":
      return (
        <rect
          key={index}
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          rx={1.5}
          fill="none"
          stroke={inkVar(shape.ink)}
          strokeWidth={2.5}
        />
      );
    default:
      return null;
  }
}

export interface MahjongTileProps {
  kind: TileKind;
  size?: TileSize;
  onClick?: () => void;
  disabled?: boolean;
  /** The tile just drawn. */
  fresh?: boolean;
  /** Highlighted by the coach. */
  hinted?: boolean;
  /** Part of a claim that is on offer. */
  focused?: boolean;
  /** The discard that is on the table right now. */
  latest?: boolean;
  /**
   * The tile is lying flat on the felt rather than standing in a rack: it gets
   * a contact shadow instead of a body, because you are looking down on it.
   */
  laid?: boolean;
  /** Position in its group, used only to stagger the arrival animation. */
  index?: number;
  label?: string;
}

export function MahjongTile({
  kind,
  size = "md",
  onClick,
  disabled,
  fresh,
  hinted,
  focused,
  latest,
  laid,
  index,
  label,
}: MahjongTileProps) {
  const { t, tile: tileName } = useI18n();
  const width = SIZE_WIDTH[size];
  const height = Math.round((width * TILE_HEIGHT) / TILE_WIDTH);
  const name = tileName(kind);
  const ink = faceInk(kind);
  const showIndex = size !== "xs";

  const className = [
    "tile",
    `tile--${size}`,
    laid ? "tile--laid" : "",
    fresh ? "tile--fresh" : "",
    hinted ? "tile--hinted" : "",
    focused ? "tile--focused" : "",
    latest ? "tile--latest" : "",
    onClick ? "tile--clickable" : "",
    disabled ? "tile--locked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const style = index === undefined ? undefined : ({ "--tile-index": index } as CSSProperties);

  const art = (
    <svg
      viewBox={`0 0 ${TILE_WIDTH} ${TILE_HEIGHT}`}
      width={width}
      height={height}
      role="img"
      aria-label={label ?? name}
      focusable="false"
    >
      <title>{name}</title>
      <rect x={BODY.x} y={BODY.y} width={BODY.w} height={BODY.h} rx={BODY.r} className="tile__body" />
      <rect
        x={4}
        y={4}
        width={TILE_WIDTH - 8}
        height={TILE_HEIGHT - 10}
        rx={5}
        className="tile__inlay"
      />
      {sheen()}
      {showIndex ? (
        <text x={7} y={15} className="tile__index" fill={inkVar(ink)}>
          {faceIndex(kind)}
        </text>
      ) : null}
      <g className="tile__art">{faceShapes(kind).map(renderShape)}</g>
      <path d={GLOSS_PATH} className="tile__gloss" />
      <path d={BEVEL_PATH} className="tile__bevel" />
    </svg>
  );

  if (!onClick) {
    return (
      <span className={className} title={name} data-kind={kind} style={style}>
        {art}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      title={name}
      data-kind={kind}
      style={style}
      aria-label={label ?? t("hand.discardTile", { tile: name })}
      onClick={onClick}
      disabled={disabled}
    >
      {art}
    </button>
  );
}

export interface TileBackProps {
  size?: TileSize;
  /**
   * "side" draws the same back a quarter turn round, the way an opponent's
   * rack looks from across the table. It is purely presentational: a back
   * never carries tile information in any orientation.
   */
  orientation?: TileOrientation;
  /** Lying on the felt rather than standing in a rack. */
  laid?: boolean;
  /** Position in its rack, used only to stagger the arrival animation. */
  index?: number;
}

/**
 * The Kikik back: jade, with the three-pip chip from the brand mark pressed
 * into it. Original artwork, drawn in the same viewBox as every face so a back
 * and a face are physically the same object.
 */
export function TileBack({ size = "sm", orientation = "up", laid, index }: TileBackProps) {
  const sideOn = orientation === "side";
  const width = SIZE_WIDTH[size];
  const height = Math.round((width * TILE_HEIGHT) / TILE_WIDTH);
  const face = (
    <>
      <rect
        x={1}
        y={1}
        width={TILE_WIDTH - 2}
        height={TILE_HEIGHT - 2}
        rx={7}
        className="tile__back-body"
      />
      <rect
        x={6}
        y={6}
        width={TILE_WIDTH - 12}
        height={TILE_HEIGHT - 12}
        rx={5}
        className="tile__back-inlay"
      />
      {sheen()}
      <circle cx={30} cy={30} r={6.4} className="tile__back-pip" />
      <circle cx={21} cy={52} r={6.4} className="tile__back-pip" />
      <circle cx={39} cy={52} r={6.4} className="tile__back-pip" />
      <path d={GLOSS_PATH} className="tile__gloss" />
      <path d={BEVEL_PATH} className="tile__bevel" />
    </>
  );

  const className = [
    "tile",
    `tile--${size}`,
    "tile--back",
    sideOn ? "tile--side" : "",
    laid ? "tile--laid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={className}
      aria-hidden="true"
      style={index === undefined ? undefined : ({ "--tile-index": index } as CSSProperties)}
    >
      <svg
        viewBox={sideOn ? `0 0 ${TILE_HEIGHT} ${TILE_WIDTH}` : `0 0 ${TILE_WIDTH} ${TILE_HEIGHT}`}
        width={sideOn ? height : width}
        height={sideOn ? width : height}
        focusable="false"
      >
        {sideOn ? <g transform={`translate(${TILE_HEIGHT} 0) rotate(90)`}>{face}</g> : face}
      </svg>
    </span>
  );
}
